/**
 * Dynamic Question Form
 * Renders questions generated by task analysis and collects user answers
 */

import React, { useState, useEffect } from 'react';
import { Question } from '../../hooks/useTaskExecution';

interface QuestionFormProps {
  questions: Question[];
  onSubmit: (answers: Record<string, unknown>) => void;
  loading?: boolean;
  // Prior answers to pre-fill, e.g. when the user returns from review to
  // revise their answers. When present, these take precedence over per-
  // question `default` values.
  initialAnswers?: Record<string, unknown>;
}

export function QuestionForm({
  questions,
  onSubmit,
  loading = false,
  initialAnswers,
}: QuestionFormProps) {
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => initialAnswers ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Socket-driven re-renders pass fresh `questions` / `initialAnswers`
    // references each poll; merge instead of overwriting so in-progress edits
    // survive. A `null` default means "no default" (Python None over JSON).
    setAnswers((prev) => {
      const next: Record<string, unknown> = { ...prev };
      questions.forEach((q) => {
        const hasDefault = q.default !== undefined && q.default !== null;
        if (hasDefault && !(q.id in next)) next[q.id] = q.default;
      });
      if (initialAnswers) {
        for (const [id, value] of Object.entries(initialAnswers)) {
          if (!(id in prev)) next[id] = value;
        }
      }
      return next;
    });
  }, [questions, initialAnswers]);

  const handleTextChange = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const handleSelectChange = (id: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const handleBooleanChange = (id: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: '' }));
  };

  const handleMultiselectToggle = (questionId: string, option: string) => {
    setAnswers((prev) => {
      const current = (prev[questionId] as string[]) || [];
      const updated = current.includes(option)
        ? current.filter((o) => o !== option)
        : [...current, option];
      return { ...prev, [questionId]: updated };
    });
    setErrors((prev) => ({ ...prev, [questionId]: '' }));
  };

  const collectErrors = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};
    questions.forEach((q) => {
      const val = answers[q.id];
      if (!(q.id in answers) || val === '' || val === undefined || val === null) {
        newErrors[q.id] = 'This field is required';
      }
      if (q.questionType === 'multiselect' && Array.isArray(val) && val.length === 0) {
        newErrors[q.id] = 'Select at least one option';
      }
    });
    return newErrors;
  };

  const isFormValid = Object.keys(collectErrors()).length === 0;

  const validateAnswers = (): boolean => {
    const newErrors = collectErrors();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateAnswers()) onSubmit(answers);
  };

  const fieldClass = (error?: string) =>
    [
      'w-full px-3 py-2 text-sm rounded-lg border bg-white dark:bg-gray-800',
      'text-gray-900 dark:text-white',
      'placeholder:text-gray-400 dark:placeholder:text-gray-500',
      'focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 focus:border-transparent',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-600',
    ].join(' ');

  const renderQuestion = (question: Question) => {
    const value = answers[question.id];
    const error = errors[question.id];

    if (question.questionType === 'boolean') {
      return (
        <label key={question.id} className="flex items-start gap-3 cursor-pointer">
          <div className="relative flex items-center justify-center mt-0.5">
            <input
              type="checkbox"
              checked={(value as boolean) || false}
              onChange={(e) => handleBooleanChange(question.id, e.target.checked)}
              className="peer sr-only"
              disabled={loading}
            />
            <div className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 peer-checked:bg-teal-600 peer-checked:border-teal-600 peer-focus:ring-2 peer-focus:ring-teal-500 peer-focus:ring-offset-1 dark:peer-focus:ring-offset-gray-900" />
            <svg
              className="absolute w-2.5 h-2.5 text-white pointer-events-none hidden peer-checked:block"
              viewBox="0 0 10 8"
              fill="none"
            >
              <path
                d="M1 4l2.5 2.5L9 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className="text-sm text-gray-800 dark:text-gray-200 leading-5">
            {question.question}
          </span>
        </label>
      );
    }

    if (question.questionType === 'multiselect') {
      return (
        <div key={question.id} className="mb-6">
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            {question.question}
          </label>
          {question.helpText && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{question.helpText}</p>
          )}
          <div
            className={`border rounded-lg p-3 space-y-2 ${
              error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
          >
            {question.options?.map((option) => {
              const selected = ((value as string[]) || []).includes(option);
              return (
                <label
                  key={option}
                  className="flex items-center gap-3 cursor-pointer py-1.5 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => handleMultiselectToggle(question.id, option)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    disabled={loading}
                  />
                  <span className="text-sm font-mono text-gray-900 dark:text-white">{option}</span>
                </label>
              );
            })}
          </div>
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      );
    }

    return (
      <div key={question.id} className="space-y-1.5">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {question.question}
        </label>

        {question.questionType === 'text' && (
          <input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => handleTextChange(question.id, e.target.value)}
            className={fieldClass(error)}
            placeholder="Enter your answer"
            disabled={loading}
          />
        )}

        {question.questionType === 'select' &&
          (() => {
            const selectedValue = (value as string) || '';
            const optionDescription = selectedValue && question.optionDescriptions?.[selectedValue];
            return (
              <>
                <select
                  value={selectedValue}
                  onChange={(e) => handleSelectChange(question.id, e.target.value)}
                  className={fieldClass(error)}
                  disabled={loading}
                >
                  <option value="">Select an option…</option>
                  {question.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {optionDescription && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 pl-0.5">
                    {optionDescription}
                  </p>
                )}
              </>
            );
          })()}

        {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}
      </div>
    );
  };

  // Split questions into boolean (shown as a group) and non-boolean
  const booleans = questions.filter((q) => q.questionType === 'boolean');
  const others = questions.filter((q) => q.questionType !== 'boolean');

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700/60 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700/60">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Configuration</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Answer the following questions to configure the task execution.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="px-6 py-5 space-y-5">
          {/* Non-boolean fields first */}
          {others.map(renderQuestion)}

          {/* Boolean fields grouped */}
          {booleans.length > 0 && (
            <div className="space-y-3 pt-1">{booleans.map(renderQuestion)}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setAnswers({})}
            disabled={loading}
            className="px-4 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={loading || !isFormValid}
            className="px-5 py-2 text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 dark:focus:ring-offset-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? 'Submitting…' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
